import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Layers, ShieldCheck, TrendingUp, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';
import { toast } from 'sonner';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/zwwvhn15_CTA%20PAGE%20IMAGE.png';

const setupOptions = [
  'Building a new website',
  'Existing site needs payment integration / upgrade',
  'Multiple sites/platforms',
  'Just exploring options',
];

const volumeOptions = [
  'Under €2,000',
  '€2,000 to €10,000',
  'Over €10,000',
  'Seasonal/varies significantly',
];

const integrationOptions = [
  'Simple hosted payment pages',
  'Custom API integration',
  'Not sure - need technical advice',
  'Want to compare both options',
];

const featureOptions = [
  'Recurring Billing',
  'Pay By Link',
  'Virtual Terminal',
  'Instant Remote Payments',
  'Mail Telephone Orders',
  'Tokenisation',
];

const timelineOptions = [
  'Within 2 weeks',
  'Within a month',
  'Just planning ahead',
  'ASAP - current system has issues',
];

const benefits = [
  {
    icon: Layers,
    title: 'Flexible Integration',
    body: 'Your website, your way. Choose from simple hosted pages or full API control. We support any platform with developer friendly tools and comprehensive documentation.',
  },
  {
    icon: ShieldCheck,
    title: 'Enterprise Security',
    body: "Bank level protection included. PCI compliance handled for you with fraud prevention, 3D Secure and tokenisation. Your customers' data stays secure.",
  },
  {
    icon: TrendingUp,
    title: 'Scale With Confidence',
    body: 'Built for growth. From startup to enterprise volumes, our platform scales seamlessly. Global payments and 24/7 uptime to support your business.',
  },
];

const Field = ({ label, required, children, testId }) => (
  <div className="mb-7" data-testid={testId}>
    <label className="block text-sm font-bold text-gray-800 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const Radio = ({ name, value, checked, onChange, testId }) => (
  <label
    className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg border cursor-pointer transition-all duration-150 ${
      checked
        ? 'border-[#1F4E79] bg-[#1F4E79]/5 ring-1 ring-[#1F4E79]/20'
        : 'border-gray-200 bg-white hover:border-[#1F4E79]/40 hover:bg-gray-50'
    }`}
    data-testid={testId}
  >
    <input type="radio" name={name} value={value} checked={checked} onChange={() => onChange(value)} className="accent-[#1F4E79]" />
    <span className="text-sm text-gray-800">{value}</span>
  </label>
);

const Checkbox = ({ value, checked, onChange, testId }) => (
  <label
    className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg border cursor-pointer transition-all duration-150 ${
      checked
        ? 'border-[#1F4E79] bg-[#1F4E79]/5 ring-1 ring-[#1F4E79]/20'
        : 'border-gray-200 bg-white hover:border-[#1F4E79]/40 hover:bg-gray-50'
    }`}
    data-testid={testId}
  >
    <input type="checkbox" checked={checked} onChange={() => onChange(value)} className="accent-[#1F4E79]" />
    <span className="text-sm text-gray-800">{value}</span>
  </label>
);

const GetStartedEcommerce = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    businessType: '',
    setup: '',
    volume: '',
    integration: '',
    features: [],
    timeline: '',
    name: '',
    email: '',
    phone: '',
    company: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: typeof e === 'string' ? e : e.target.value }));

  const toggleFeature = (value) =>
    setForm((f) => ({
      ...f,
      features: f.features.includes(value) ? f.features.filter((x) => x !== value) : [...f.features, value],
    }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.businessType || !form.setup || !form.volume || !form.integration || !form.timeline || !form.name || !form.email || !form.phone) {
      toast.error('Please complete all required fields.');
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      toast.success("Thanks! We'll be in touch within 24 hours to discuss your eCommerce integration.");
      setForm({ businessType: '', setup: '', volume: '', integration: '', features: [], timeline: '', name: '', email: '', phone: '', company: '' });
      setSubmitting(false);
    }, 700);
  };

  return (
    <div className="min-h-screen flex flex-col relative" data-testid="get-started-ecommerce-page">
      <div className="fixed inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${HERO_IMAGE})` }} aria-hidden="true" />
      <div className="fixed inset-0 bg-gradient-to-br from-[#0a1929]/82 via-[#0a1929]/68 to-[#163a5f]/78" aria-hidden="true" />

      <div className="relative z-10 flex flex-col min-h-screen">
        <Header />
        <div className="h-20" />

        <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-8 pb-16">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/85 hover:text-white font-medium mb-8 transition-colors duration-200"
            data-testid="get-started-ecommerce-back"
          >
            <ArrowLeft size={18} />
            Back
          </button>

          <div className="max-w-4xl mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ef5b25]/20 border border-[#ef5b25]/40 text-[#ffcba6] text-[11px] font-bold uppercase tracking-[0.15em] mb-5">
              Free Consultation — Transparent Pricing
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] tracking-tight mb-4" data-testid="gse-title">
              Secure Online Payment<br />Integration Made Simple
            </h1>
            <p className="text-lg sm:text-xl text-white/90 leading-relaxed max-w-3xl font-medium">
              Launch your first online store or upgrade existing payments with our suite of eCommerce solutions. Get expert guidance on APIs, hosted pages and security — completely free with transparent pricing.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <form onSubmit={handleSubmit} className="lg:col-span-8 bg-white rounded-xl shadow-2xl p-7 sm:p-9" data-testid="gse-form">
              <Field label="1. What type of online business do you operate?" required testId="gse-field-business">
                <input
                  type="text"
                  value={form.businessType}
                  onChange={update('businessType')}
                  placeholder="e.g. retail store, subscription service, digital products, marketplace"
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none transition-all duration-150"
                  data-testid="gse-input-business"
                />
              </Field>

              <Field label="2. What's your current eCommerce setup?" required testId="gse-field-setup">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {setupOptions.map((opt, i) => (
                    <Radio key={opt} name="setup" value={opt} checked={form.setup === opt} onChange={update('setup')} testId={`gse-setup-${i}`} />
                  ))}
                </div>
              </Field>

              <Field label="3. What's your monthly transaction volume?" required testId="gse-field-volume">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {volumeOptions.map((opt, i) => (
                    <Radio key={opt} name="volume" value={opt} checked={form.volume === opt} onChange={update('volume')} testId={`gse-volume-${i}`} />
                  ))}
                </div>
              </Field>

              <Field label="4. What type of integration do you prefer?" required testId="gse-field-integration">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {integrationOptions.map((opt, i) => (
                    <Radio key={opt} name="integration" value={opt} checked={form.integration === opt} onChange={update('integration')} testId={`gse-integration-${i}`} />
                  ))}
                </div>
              </Field>

              <Field label="5. Do you need additional features? (Select any that apply)" testId="gse-field-features">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {featureOptions.map((opt, i) => (
                    <Checkbox key={opt} value={opt} checked={form.features.includes(opt)} onChange={toggleFeature} testId={`gse-feature-${i}`} />
                  ))}
                </div>
              </Field>

              <Field label="6. When do you want to go live?" required testId="gse-field-timeline">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {timelineOptions.map((opt, i) => (
                    <Radio key={opt} name="timeline" value={opt} checked={form.timeline === opt} onChange={update('timeline')} testId={`gse-timeline-${i}`} />
                  ))}
                </div>
              </Field>

              <div data-testid="gse-field-contact">
                <label className="block text-sm font-bold text-gray-800 mb-3">7. Contact Details</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input type="text" value={form.name} onChange={update('name')} placeholder="Contact name *" className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none" data-testid="gse-input-name" />
                  <input type="email" value={form.email} onChange={update('email')} placeholder="Email address *" className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none" data-testid="gse-input-email" />
                  <input type="tel" value={form.phone} onChange={update('phone')} placeholder="Phone number *" className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none" data-testid="gse-input-phone" />
                  <input type="text" value={form.company} onChange={update('company')} placeholder="Business / website name (optional)" className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none" data-testid="gse-input-company" />
                </div>
              </div>

              <button type="submit" disabled={submitting} className="group mt-7 inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#1F4E79] text-white font-bold rounded-lg hover:bg-[#163a5f] hover:gap-3.5 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed" data-testid="gse-submit">
                {submitting ? 'Sending…' : 'Request Consultation'}</button>

              <p className="text-xs text-gray-500 mt-4">
                <CheckCircle2 size={12} className="inline mr-1 text-emerald-600" />
                Your information is secure and used only to prepare your free eCommerce consultation.
              </p>
            </form>

            <aside className="lg:col-span-4 space-y-4 lg:pt-16" data-testid="gse-benefits">
              {benefits.map((b, i) => {
                const Icon = b.icon;
                return (
                  <div key={i} className="rounded-xl p-5 bg-white/10 backdrop-blur-md border border-white/20 shadow-xl hover:bg-white/15 transition-all duration-200" data-testid={`gse-benefit-${i}`}>
                    <div className="flex items-center gap-2.5 mb-2">
                      <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center border border-white/20">
                        <Icon size={18} className="text-white" strokeWidth={1.75} />
                      </div>
                      <h3 className="text-[15px] font-bold text-white leading-tight">{b.title}</h3>
                    </div>
                    <p className="text-[13px] text-white/85 leading-relaxed">{b.body}</p>
                  </div>
                );
              })}
            </aside>
          </div>
        </main>

        <Footer />
        <BackToTop />
      </div>
    </div>
  );
};

export default GetStartedEcommerce;
